/**
 * Staff Work — Inbox (the chronicle).
 *
 * A reflection of the same calendar union (every obligation across sources for
 * the current user), shown as a feed. Filter by state (All / Pending / Upcoming
 * / Completed); sort by due date (worst-first) or posted date (oldest-first).
 * Completed items always sink to the bottom. A row opens the underlying item.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Search, RefreshCw, Clock, CalendarClock, Filter } from "lucide-react";
import Skeleton from "../../../components/common/Skeleton";
import NoDataFound from "../../../components/common/NoDataFound";
import FormControl from "../../../components/common/FormControl";
import Toast, { ToastMessage } from "../../../components/common/Toast";
import useOutsideClick from "../../../components/common/useOutsideClick";
import extractApiError from "../../../utils/apiError";
import { useEntitySettings } from "../../../contexts/EntitySettingsContext";
import { inboxService, InboxItem, InboxState, InboxBucket } from "../../../services/inboxService";
import { tasksService, Task } from "../../../services/tasksService";
import TaskActionPanel from "../../../components/tasks/TaskActionPanel";

type FilterKey = "all" | InboxState;
type SortKey = "due" | "posted";

const FILTERS: { key: FilterKey; label: string }[] = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "upcoming", label: "Upcoming" },
    { key: "completed", label: "Completed" },
];

const BUCKET_META: Record<InboxBucket, { label: string; cls: string; accent: string }> = {
    overdue:   { label: "Overdue",   cls: "status-badge sm danger",  accent: "#dc2626" },
    due:       { label: "Due",       cls: "status-badge sm warning", accent: "#f59e0b" },
    upcoming:  { label: "Upcoming",  cls: "status-badge sm info",    accent: "#3b82f6" },
    completed: { label: "Completed", cls: "status-badge sm success", accent: "#16a34a" },
};

const WorkInboxBody: React.FC = () => {
    const navigate = useNavigate();
    const { formatDate } = useEntitySettings();
    const fmtPosted = (iso: string | null): string => {
        if (!iso) return "—";
        return formatDate(iso);
    };
    const fmtDue = (iso: string | null): string => {
        if (!iso) return "—";
        return formatDate(iso);
    };
    const [items, setItems] = useState<InboxItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<FilterKey>("all");
    // Default = posting order (the immutable chain: when you became aware).
    const [sortBy, setSortBy] = useState<SortKey>("posted");
    const [typeFilter, setTypeFilter] = useState<string>("all");   // source-type dimension
    const [searchTerm, setSearchTerm] = useState("");
    const [toast, setToast] = useState<ToastMessage | null>(null);
    // Plain TASK items open the action panel in place (same popup as the Action
    // Centre kanban) instead of navigating away to /work.
    const [selectedTask, setSelectedTask] = useState<Task | null>(null);

    // Toolbar controls (type / sort / search / refresh) live in a popover behind
    // the Filter icon, which sits immediately to the right of the state tabs.
    const [filtersOpen, setFiltersOpen] = useState(false);
    const filterPopRef = useRef<HTMLDivElement | null>(null);
    const filterBtnRef = useRef<HTMLButtonElement | null>(null);
    useOutsideClick(filterPopRef, () => setFiltersOpen(false), [filterBtnRef]);

    const refresh = async () => {
        setLoading(true);
        try {
            setItems(await inboxService.list());
        } catch (e: any) {
            setToast({ type: "error", message: extractApiError(e, "Failed to load inbox") });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { refresh(); }, []);

    // Row click: TASK source opens the action-panel popup in place; everything
    // else navigates to its own detail surface as before.
    const openItem = async (it: InboxItem) => {
        if (it.source === "TASK") {
            try {
                setSelectedTask(await tasksService.get(it.source_id));
            } catch (e: any) {
                setToast({ type: "error", message: extractApiError(e, "Failed to open task") });
            }
        } else if (it.route_path) {
            navigate(it.route_path);
        }
    };

    const counts = useMemo(() => {
        const c: Record<FilterKey, number> = { all: items.length, pending: 0, upcoming: 0, completed: 0 };
        for (const i of items) c[i.state] += 1;
        return c;
    }, [items]);

    // Distinct obligation types present (source -> label), for the Type dropdown.
    const typeOptions = useMemo(() => {
        const m = new Map<string, string>();
        for (const i of items) if (!m.has(i.source)) m.set(i.source, i.source_label || i.source);
        return Array.from(m, ([source, label]) => ({ source, label })).sort((a, b) => a.label.localeCompare(b.label));
    }, [items]);

    const visible = useMemo(() => {
        const term = searchTerm.toLowerCase();
        const filtered = items.filter(i =>
            (filter === "all" || i.state === filter) &&
            (typeFilter === "all" || i.source === typeFilter) &&
            (!term ||
                i.title?.toLowerCase().includes(term) ||
                i.source_label?.toLowerCase().includes(term))
        );
        // Sort is a single uniform dimension across ALL items — the chain order
        // is never disturbed (completed is NOT pushed down; filters only remove).
        const key = (i: InboxItem) =>
            sortBy === "due" ? (i.due_date || "9999-12-31") : (i.created_on || "9999");
        return [...filtered].sort((a, b) => {
            const ka = key(a), kb = key(b);          // ascending (oldest / earliest first)
            return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
    }, [items, filter, typeFilter, sortBy, searchTerm]);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header" style={{ gap: 8, flexWrap: "wrap", paddingTop: "0", paddingBottom : "0" }}>
                <div className="flex items-center gap-1">
                    {FILTERS.map(f => (
                        <button
                            key={f.key}
                            type="button"
                            data-testid={`BTN_inbox_filter_${f.key}`}
                            className={`app-modal-tab${filter === f.key ? " app-modal-tab--active" : ""}`}
                            style={filter !== f.key ? { borderBottom: "none", marginBottom: 0 } : { marginBottom: 0 }}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label} <span className="text-xs text-gray-400">({counts[f.key]})</span>
                        </button>
                    ))}
                </div>

                {/* Filter icon — sits on the right. Click reveals
                    the type / sort / search / refresh controls in a popover. */}
                <div style={{ position: "relative", marginLeft: "auto" }}>
                    <button
                        ref={filterBtnRef}
                        type="button"
                        data-testid="BTN_inbox_filter_toggle"
                        className="btn-icon"
                        style={filtersOpen ? { background: "#eef2ff", color: "#4f46e5" } : undefined}
                        title="Filters & sort"
                        aria-label="Filters & sort"
                        aria-expanded={filtersOpen}
                        onClick={() => setFiltersOpen(o => !o)}
                    >
                        <Filter size={16} />
                    </button>

                    {filtersOpen && (
                        <div
                            ref={filterPopRef}
                            data-testid="POP_inbox_filters"
                            role="dialog"
                            style={{
                                position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 30,
                                minWidth: 240, padding: 12, display: "flex", flexDirection: "column", gap: 10,
                                background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            }}
                        >
                            <FormControl.Input
                                data-testid="INP_work_inbox_search"
                                type="text"
                                placeholder="Search…"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                leadingIcon={<Search size={14} />}
                                className="filter-search"
                            />

                            <FormControl.Select
                                data-testid="SEL_inbox_type_filter"
                                value={typeFilter}
                                onChange={e => {
                                    setTypeFilter(e.target.value);
                                    setSearchTerm("");
                                }}
                                noMargin
                                style={{ width: "100%" }}
                            >
                                <option value="all">All types</option>
                                {typeOptions.map(o => (
                                    <option key={o.source} value={o.source}>{o.label}</option>
                                ))}
                            </FormControl.Select>

                            {/* Sort — modern two-option segmented toggle */}
                            <div className="seg-toggle" role="radiogroup" aria-label="Sort by" style={{ width: "100%" }}>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={sortBy === "posted"}
                                    data-testid="BTN_inbox_sort_posted"
                                    className={`seg-toggle-btn${sortBy === "posted" ? " seg-toggle-btn--on" : ""}`}
                                    style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                                    onClick={() => setSortBy("posted")}
                                    title="Sort by posted date"
                                >
                                    <Clock size={14} />
                                    <span>Posted</span>
                                </button>
                                <button
                                    type="button"
                                    role="radio"
                                    aria-checked={sortBy === "due"}
                                    data-testid="BTN_inbox_sort_due"
                                    className={`seg-toggle-btn${sortBy === "due" ? " seg-toggle-btn--on" : ""}`}
                                    style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4 }}
                                    onClick={() => setSortBy("due")}
                                    title="Sort by due date"
                                >
                                    <CalendarClock size={14} />
                                    <span>Due</span>
                                </button>
                            </div>

                            <button
                                type="button"
                                className="inline-flex items-center justify-center gap-1 h-[30px] px-2 text-xs font-medium text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
                                title="Refresh"
                                aria-label="Refresh"
                                onClick={refresh}
                            >
                                <RefreshCw size={14} />
                                <span>Refresh</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* grey rule under the toolbar — matches the vertical pane separator
                and the calendar's header rule so the line reads continuously
                across both panes. */}
 
            <div className="netbody-content">
                <div className="scroll-container">
                    {loading && (
                        <div className="action-card-grid">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="action-card action-card--skeleton">
                                    <div className="action-card__skel-header">
                                        <Skeleton.Box width={34} height={34} radius={8} />
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                                            <Skeleton.Box width="60%" height={12} />
                                            <Skeleton.Box width="40%" height={10} />
                                        </div>
                                        <Skeleton.Pill width={60} height={20} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!loading && visible.length === 0 && (
                        <NoDataFound
                            title={searchTerm || filter !== "all" ? "No Matching Items" : "Inbox Empty"}
                            message={searchTerm || filter !== "all"
                                ? "No items match this filter."
                                : "Nothing has entered your chronicle yet."}
                        />
                    )}

                    {!loading && visible.length > 0 && (
                        <div className="inbox-chain" style={{ position: "relative", padding: "4px 0" }}>
                            {visible.map((it, idx) => {
                                const m = BUCKET_META[it.bucket];
                                const isFirst = idx === 0;
                                const isLast = idx === visible.length - 1;
                                return (
                                    <div
                                        key={`${it.source}-${it.source_id}`}
                                        className="inbox-bead-row"
                                        style={{ display: "flex", alignItems: "stretch", cursor: (it.source === "TASK" || it.route_path) ? "pointer" : "default" }}
                                        onClick={() => openItem(it)}
                                        data-testid={`bead_work_inbox_${it.source}_${it.source_id}`}
                                    >
                                        {/* rail: continuous vertical line + colored bead */}
                                        <div style={{ position: "relative", width: 30, flexShrink: 0 }}>
                                            <span style={{
                                                position: "absolute", left: 14, width: 2,
                                                top: isFirst ? 18 : 0, bottom: isLast ? "calc(100% - 18px)" : 0,
                                                background: "#e5e7eb",
                                            }} />
                                            <span title={m.label} style={{
                                                position: "absolute", left: 10, top: 13, width: 10, height: 10,
                                                borderRadius: "50%", background: it.is_done ? "#fff" : m.accent,
                                                border: `2px solid ${m.accent}`, boxShadow: "0 0 0 2px #fff",
                                            }} />
                                        </div>
                                        {/* body */}
                                        <div className="inbox-bead-body" style={{
                                            flex: 1, padding: "8px 4px 12px", borderBottom: "1px solid #f1f5f9", minWidth: 0,
                                        }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <FileText size={13} style={{ opacity: 0.5, flexShrink: 0 }} />
                                                <span className="table-list-cell-title" style={{
                                                    flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    textDecoration: it.is_done ? "line-through" : undefined,
                                                    opacity: it.is_done ? 0.65 : 1,
                                                }}>
                                                    {it.title || "—"}
                                                </span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4, fontSize: 11, color: "#6b7280", flexWrap: "nowrap", minWidth: 0 }}>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{it.source_label}</span>
                                                <span title="Posted" style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", flexShrink: 0 }}>
                                                    <Clock size={11} />{fmtPosted(it.created_on)}
                                                </span>
                                                <span
                                                    title="Due"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", flexShrink: 0, ...(it.bucket === "overdue" ? { color: "#dc2626" } : {}) }}
                                                >
                                                    <CalendarClock size={11} />{fmtDue(it.due_date)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {selectedTask && (
                <TaskActionPanel
                    task={selectedTask}
                    isOpen={!!selectedTask}
                    onClose={() => setSelectedTask(null)}
                    onMutated={() => { setSelectedTask(null); refresh(); }}
                />
            )}

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default WorkInboxBody;
